import Button from '@components/v2/ui/Button/Button';
import Typography from '@components/v2/ui/Typography/Typography';
import { Grid } from '@mui/material';
import * as R from 'ramda';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { useShallow } from 'zustand/react/shallow';
import LangEnum from '../../lang/LangEnum';
import PATHS from '../../routes/routes.const';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import { ActiveOrderTestIds } from '../../utils/testIds/ActiveOrder.testIds';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../translation/TranslationContext';
import OrderBookCard from './OrderBookCard';
import useOrderBook from './hooks/useOrderBook';

const useStyles = makeStyles()((theme) => ({
  cardListContainer: {
    overflowY: 'auto',
  },
  spacement: {
    padding: theme.spacing(0, 3, 2, 3),
  },
}));

const OrderBookList: React.FC = () => {
  const { classes } = useStyles();
  const [
    selectedOrder,
    orders,
    handleRemoveAllOrders,
    handleRemoveSelectedOrder,
    handleFinishSelectedOrder,
  ] = useOrderBookStore(
    useShallow((state) => [
      state.selectedOrder,
      state.orders,
      state.handleRemoveAllOrders,
      state.handleRemoveSelectedOrder,
      state.handleFinishSelectedOrder,
    ]),
  );
  const { team } = useApplicationContext();
  const { onEdit, closeOrderBook, selectedOrderIsSuggested } = useOrderBook();
  const { translate } = useTranslation();
  const history = useHistory();

  if (!selectedOrder) {
    return null;
  }
  const orderBookIsEmpty = R.isEmpty(orders);

  const handleRemoverOrders = () => {
    if (orderBookIsEmpty) {
      handleRemoveSelectedOrder(team);
      closeOrderBook();
      if (onEdit) {
        history.push(`${PATHS.main.orders.path}`);
      }
    } else {
      handleRemoveAllOrders(team);
    }
  };

  const removeSuggestedOrder = () => {
    handleFinishSelectedOrder(team?.id);
  };

  return (
    <Grid container spacing={1} sx={orderBookIsEmpty ? { my: 0.5 } : { mb: 1 }}>
      {orderBookIsEmpty ? (
        <Grid size={12}>
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <Button
              variant="text"
              color="primary"
              size="small"
              testId={ActiveOrderTestIds.ORDER_BOOK_FINISH_ORDER_BUTTON}
              onClick={handleRemoverOrders}
            >
              {translate(LangEnum.GLOBAL_FINISH_EDITING)}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid size={12}>
          <Grid
            container
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid size="grow">
              <Typography variant="overline" color="textSecondary">
                {translate(LangEnum.ORDER_BOOK_OTHER_ORDERS)}
              </Typography>
            </Grid>
            <Grid>
              <Button
                variant="text"
                color="primary"
                size="small"
                testId={ActiveOrderTestIds.ORDER_BOOK_FINISH_ORDER_BUTTON}
                onClick={selectedOrderIsSuggested ? removeSuggestedOrder : handleRemoverOrders}
              >
                {translate(
                  selectedOrderIsSuggested
                    ? LangEnum.GLOBAL_FINISH_EDITING
                    : LangEnum.ORDER_BOOK_REMOVE_BOOK_ALL,
                )}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
      {orders.length > 0 && (
        <Grid size={12}>
          <Grid
            container
            className={classes.cardListContainer}
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            {orders.map((order) => (
              <Grid
                key={order.id}
                style={{ display: selectedOrder?.id === order?.id ? 'none' : 'block' }}
                size={12}
              >
                <OrderBookCard order={order} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default OrderBookList;
